import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * useBarcodeScanner — listens for USB/Bluetooth HID barcode scanner input
 * (rapid keystrokes ending with Enter) anywhere on the document.
 * Calls onScan(code) when a barcode is detected.
 * Ignores input when an INPUT or TEXTAREA element is focused.
 */
export function useBarcodeScanner(onScan) {
    const bufRef   = useRef('');
    const timerRef = useRef(null);
    const cbRef    = useRef(onScan);
    cbRef.current  = onScan;

    useEffect(() => {
        const handleKey = (e) => {
            const tag = document.activeElement?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

            if (e.key === 'Enter') {
                const val = bufRef.current.trim();
                bufRef.current = '';
                if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
                if (val.length >= 2) cbRef.current(val);
            } else if (e.key.length === 1) {
                bufRef.current += e.key;
                if (timerRef.current) clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => { bufRef.current = ''; }, 100);
            }
        };
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('keydown', handleKey);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);
}

export function useIsMobile() {
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);
    return isMobile;
}

/**
 * useIdleLock — fires `onIdle` after `timeoutMs` of no user activity.
 * Listens for touch, pointer, key, and mouse events on the whole document.
 * Used by the fullscreen portal pages to bounce an idle worker back to the
 * PIN login screen (data in the DB is already saved per-action, so nothing
 * is lost on lock).
 */
export function useIdleLock(enabled, timeoutMs, onIdle) {
    const cbRef    = useRef(onIdle);
    cbRef.current  = onIdle;
    const timerRef = useRef(null);

    useEffect(() => {
        if (!enabled) return undefined;
        const reset = () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                try { cbRef.current?.(); } catch { /* ignore */ }
            }, timeoutMs);
        };
        const EVENTS = ['pointerdown', 'keydown', 'touchstart', 'mousemove', 'wheel', 'scroll'];
        EVENTS.forEach(ev => document.addEventListener(ev, reset, { passive: true }));
        reset(); // start the timer on mount
        return () => {
            EVENTS.forEach(ev => document.removeEventListener(ev, reset));
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [enabled, timeoutMs]);
}
